import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  Popover,
  Stack,
  Typography,
} from "@mui/material";

import { NotificationTrayHeader, type TrayFilter } from "./NotificationTrayHeader";
import { NotificationTrayRow, type TrayNotification } from "./NotificationTrayRow";

function NotificationTrayBody({
  notifications,
  loading,
  filter,
  rowActionsNotificationUserId,
  onOpenNotification,
  onRowActionsClick,
}: {
  notifications: TrayNotification[];
  loading: boolean;
  filter: TrayFilter;
  rowActionsNotificationUserId: string | null;
  onOpenNotification: (notificationUser: TrayNotification) => void;
  onRowActionsClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    notificationUserId: string
  ) => void;
}) {
  const isInitialLoading = loading && notifications.length === 0;

  return (
    <Box sx={{ overflow: "auto", flex: "1 1 auto", minHeight: 120 }}>
      {isInitialLoading ? (
        <Stack
          alignItems="center"
          data-testid="notifications-tray-body-loading"
          justifyContent="center"
          sx={{ py: 4 }}
        >
          <CircularProgress size={24} />
        </Stack>
      ) : null}
      {!isInitialLoading && notifications.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {filter === "unread" ? "No unread notifications" : "No notifications"}
        </Typography>
      ) : null}
      <List disablePadding>
        {notifications.map((notificationUser) => (
          <NotificationTrayRow
            key={notificationUser.id}
            notificationUser={notificationUser}
            actionsOpen={rowActionsNotificationUserId === notificationUser.id}
            onOpen={() => onOpenNotification(notificationUser)}
            onActionsClick={(event) => onRowActionsClick(event, notificationUser.id)}
          />
        ))}
      </List>
    </Box>
  );
}

const TRAY_MAX_HEIGHT_PX = 560;
/** Room for the header, footer and a couple of rows, however little space is left. */
const TRAY_MIN_HEIGHT_PX = 240;
const TRAY_VIEWPORT_MARGIN_PX = 16;

/**
 * Fits under the bell, so a short or zoomed window scrolls the list instead of hiding
 * the footer. The viewport height stays live in CSS, so zooming or resizing with the
 * tray open re-fits it; the bell's offset is fixed in CSS pixels at any zoom.
 */
function trayMaxHeight(anchorEl: HTMLElement | null) {
  if (!anchorEl) {
    return TRAY_MAX_HEIGHT_PX;
  }
  const reserved = Math.round(anchorEl.getBoundingClientRect().bottom) + TRAY_VIEWPORT_MARGIN_PX;
  return `max(${TRAY_MIN_HEIGHT_PX}px, min(${TRAY_MAX_HEIGHT_PX}px, calc(100dvh - ${reserved}px)))`;
}

/** The bell's popover: a page of recent notifications, with ways out to the inbox and settings. */
export function NotificationTray({
  anchorEl,
  onClose,
  filter,
  onFilterChange,
  notifications,
  loading,
  rowActionsNotificationUserId,
  onOpenNotification,
  onRowActionsClick,
  onTopActionsClick,
  onShowAll,
  onSettings,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  filter: TrayFilter;
  onFilterChange: (filter: TrayFilter) => void;
  notifications: TrayNotification[];
  loading: boolean;
  rowActionsNotificationUserId: string | null;
  onOpenNotification: (notificationUser: TrayNotification) => void;
  onRowActionsClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    notificationUserId: string
  ) => void;
  onTopActionsClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onShowAll: () => void;
  /** Absent when the reader has no settings page to go to. */
  onSettings: (() => void) | null;
}) {
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: { width: 420, maxWidth: "calc(100vw - 24px)" },
        },
      }}
    >
      <Stack spacing={0} sx={{ maxHeight: trayMaxHeight(anchorEl) }}>
        <NotificationTrayHeader
          filter={filter}
          onFilterChange={onFilterChange}
          refreshing={loading && notifications.length > 0}
          onActionsClick={onTopActionsClick}
        />
        <Divider />
        <NotificationTrayBody
          notifications={notifications}
          loading={loading}
          filter={filter}
          rowActionsNotificationUserId={rowActionsNotificationUserId}
          onOpenNotification={onOpenNotification}
          onRowActionsClick={onRowActionsClick}
        />
        <Divider />
        {/*
          Two footer actions, opposite ends of a row that already existed: "Show
          all" goes to the inbox, and settings is the other thing a reader wants
          from here. Put in the footer rather than the kebab because it is
          navigation like its neighbour, not a bulk action on the notifications
          themselves, and because a link nobody has to open a menu to find is one
          fewer question for support.
        */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Button
            size="small"
            onClick={onShowAll}
            sx={{
              fontWeight: 700,
              px: 2,
              py: 1.5,
              textTransform: "uppercase",
            }}
          >
            Show all
          </Button>
          {onSettings ? (
            <Button
              size="small"
              startIcon={<SettingsOutlined />}
              onClick={onSettings}
              sx={{ px: 2, py: 1.5 }}
            >
              Settings
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Popover>
  );
}
